import { Button, Icons, Intent, Size } from "construct-ui";
import CodeByCategorySelectionComponent from "../../components/codemaster/codemaster-category-search-select.component";
import DatePickerComponent from "../../components/date-picker/date-picker.component";
import ProductSelectionComponent from "../../components/model-management/product-selection.component";
import paginationComponent from "../../components/pagination/pagination.component";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import codeMasterModel from "../../models/common/code-master.model";
import ModelManagementModel from "../../models/model-management/model-management.model";
import ViewBase from "../view-base.view";
import ModelInformationDialog from "./model-information.dialog";

/**
 * Model Management View
 * 
 * View page that displays all models and their respsective product group
 * 
 * @author Darrus Goh
 */

export default function ModelManagementView() {
    ViewBase.call(this);

    const DEFAULT_STATUS = {
        sequence: "1",
        code: "0001",
        name: "Activity"
    };

    const DEFAULT_FILTERS = {
        product: [],
        brand: [],
        modelCode: '',
        description: '',
        createDateFrom: '',
        createDateTo: '',
        statusCode: DEFAULT_STATUS
    };

    const DEFAULT_PAGINATION = {
        pageSize: 10,
        pageIndex: 1,
        totalItems: 0
    };

    let filters = structuredClone(DEFAULT_FILTERS);
    let currentSearchParams = structuredClone(DEFAULT_FILTERS);
    let pagination = structuredClone(DEFAULT_PAGINATION);

    let models = [];
    let statuses = [];
    let selectedModelCode = '';
    let isModelInformationOpen = false;

    const toQueryParams = (params) => {
        return Object.assign(
            {
                pageSize: pagination.pageSize,
                pageIndex: pagination.pageIndex,
                product: params.product,
                brand: params.brand.map(({ code }) => code),
                modelCode: params.modelCode,
                description: params.description,
                createDateFrom: params.createDateFrom,
                createDateTo: params.createDateTo,
                statusCode: params.statusCode.code
            },
        );
    };

    const handleFormSubmit = (e) => {
        if (!this.validateForm(e)) return false;

        pagination = structuredClone(DEFAULT_PAGINATION);
        currentSearchParams = structuredClone(filters)
        getModelList();
    }

    const getModelList = () => {
        this.showLoading();
        models = [];
        ModelManagementModel.getModelList(toQueryParams(currentSearchParams))
            .then((result) => {
                if (result.data) {
                    models = result.data;
                    pagination.pageIndex = result.pageIndex;
                    pagination.pageSize = result.pageSize;
                    pagination.totalItems = result.total;
                }
            })
            .catch((error) => {
                this.handleError(error)
            })
            .finally(() => {
                this.hideLoading();
            })
    }

    const getStatuses = () => {
        codeMasterModel.getByCategoryV2({ category: 'ModelStatus' })
            .then((result) => {
                statuses = result
            })
            .catch((error) => {
                this.handleError(error);
            })
    }

    const downloadModelList = () => {
        this.showLoading();
        ModelManagementModel.downloadModelList({ ...currentSearchParams, brand: currentSearchParams.brand.map(({ code }) => code), statusCode: currentSearchParams.statusCode.code })
            .catch((error => {
                this.handleError(error);
            }))
            .finally(() => {
                this.hideLoading();
            })
    }

    const clearFilter = () => {
        filters = structuredClone(DEFAULT_FILTERS);
        pagination = structuredClone(DEFAULT_PAGINATION);
    }

    const openModelInformation = (modelCode) => {
        selectedModelCode = modelCode;
        isModelInformationOpen = true;
    }

    const closeModelInformation = () => {
        isModelInformationOpen = false;
    }

    return {
        oninit: () => {
            getStatuses();
        },
        view: () => {
            return [
                /* SEARCH */
                m('.card.mb-3',
                    m('.card-body',
                        m('form.needs-validation[novalidate].row.g-2', { onsubmit: handleFormSubmit },
                            m('.col-md-3.col-lg-3.position-relative',
                                m('label.form-label.mandatory[for="productSelection"]', this.getText('LT00000579', 'Product')),
                                m(ProductSelectionComponent, {
                                    id: 'productSelection',
                                    required: true,
                                    selectedItems: filters.product,
                                    onChange: (selectedItems) => {
                                        filters.product = selectedItems;
                                    }
                                }),
                                m('.invalid-tooltip', this.getText('EM00000787', 'Please choose a product.'))
                            ),
                            m('.col-md-3.col-lg-3.position-relative',
                                m('label.form-label[for="brandSelection"]', this.getText('LT00000894', 'Brand')),
                                m(CodeByCategorySelectionComponent, {
                                    id: 'brandSelection',
                                    title: this.getText('LT00000894', 'Brand'),
                                    category: 'MCSBrand',
                                    selectedItems: filters.brand,
                                    onSelect: (selectedItems) => {
                                        filters.brand = selectedItems;
                                    }
                                })
                            ),
                            m('.col-md-3.col-lg-3.position-relative',
                                m('label.form-label[for="modelCodeInput"]', this.getText('LT00000728', 'Model Code')),
                                m('input#modelCodeInput', {
                                    class: 'form-control form-control-bg',
                                    placeholder: this.getText('LT00000728', 'Model Code'),
                                    type: 'text',
                                    value: filters.modelCode,
                                    onchange: (e) => { filters.modelCode = e.target.value }
                                })
                            ),
                            m('.col-md-3.col-lg-3.position-relative',
                                m('label.form-label[for="descriptionInput"]', this.getText('LT00000012', 'Description')),
                                m('input#descriptionInput', {
                                    class: 'form-control form-control-bg',
                                    placeholder: this.getText('LT00000012', 'Description'),
                                    type: 'text',
                                    value: filters.description,
                                    onchange: (e) => { filters.description = e.target.value }
                                })
                            ),
                            m('.col-md-3.col-lg-3.position-relative',
                                m('label.form-label[for="datePicker"]', this.getText('LT00000041', 'Create Date')),
                                m(DatePickerComponent, {
                                    id: 'datePicker',
                                    class: 'form-control',
                                    placeholder: this.getText('LT00000895', 'Time Range'),
                                    isRange: true,
                                    dateStart: filters.createDateFrom,
                                    dateEnd: filters.createDateTo,
                                    onselect: (event) => {
                                        filters.createDateFrom = event.detail.start;
                                        filters.createDateTo = event.detail.end;
                                    }
                                })
                            ),
                            m('.col-md-3.col-lg-3.position-relative',
                                m('label.form-label[for="statusSelect"]', this.getText('LT00000781', 'Status')),
                                m(SingleSelectComponent, {
                                    id: 'statusSelect',
                                    items: statuses,
                                    selectedItem: filters.statusCode,
                                    onSelect: (selectedItem) => {
                                        filters.statusCode = selectedItem
                                    }
                                })
                            ),
                            m('.col-12.d-flex.justify-content-end.gap-2',
                                m('button.btn.btn-primary[type="submit"]', this.getText("LT00000001", "Search")),
                                m('button.btn.btn-white',
                                    {
                                        onclick: () => {
                                            clearFilter();
                                        }
                                    },
                                    this.getText("LT00000031", "Clear")
                                )
                            )
                        )
                    )
                ),


                /* TABLE */
                m('.card',
                    m('.card-body',
                        m('.row.g-2',
                            m('.col-12.d-flex.justify-content-end',
                                m('button.btn.btn-primary', {
                                    onclick: downloadModelList,
                                    disabled: models.length === 0
                                },
                                    m('i.bi.bi-file-earmark-arrow-down-fill'),
                                    this.getText('LT00000018', 'Excel Download'))
                            ),
                            m('.col-12',
                                m('.table-responsive',
                                    m('table.table',
                                        m('thead',
                                            m('tr.align-middle.center',
                                                m('th', this.getText('LT00000894', 'Brand')),
                                                m('th', this.getText('LT00000728', 'Model Code')),
                                                m('th', this.getText('LT00000012', 'Description')),
                                                m('th', this.getText('LT00000246', 'Division Group')),
                                                m('th', this.getText('LT00000233', 'Division')),
                                                m('th', this.getText('LT00000862', 'Product Group')),
                                                m('th', this.getText('LT00000579', 'Product')),
                                                m('th', this.getText('LT00000781', 'Status')),
                                                m('th', this.getText('LT00000108', 'Details'))
                                            )
                                        ),
                                        m('tbody',
                                            models.map((data,) => {
                                                const active = data.status === 'Activity';

                                                return m('tr.align-middle.center',
                                                    m('td', m('span.cursor-text', data.brand)),
                                                    m('td', m('span.cursor-text', data.modelCode)),
                                                    m('td', m('span.cursor-text', data.description)),
                                                    m('td', m('span.cursor-text', data.divisionGroup)),
                                                    m('td', m('span.cursor-text', data.division)),
                                                    m('td', m('span.cursor-text', data.productGroup)),
                                                    m('td', m('span.cursor-text', data.product)),
                                                    m('td', m(`span.badge.rounded-pill${active ? '.green-bg-status' : '.danger-bg-status'}`, active ? this.getText('LT00000025', 'Active') : this.getText('', 'Inactive'))),
                                                    m('td',
                                                        m(Button, {
                                                            style: 'background: #009ef7;border-color: transparent;',
                                                            iconLeft: Icons.FILE_TEXT,
                                                            intent: Intent.PRIMARY,
                                                            size: Size.XS,
                                                            onclick: () => { openModelInformation(data.modelCode); }
                                                        })
                                                    )
                                                );
                                            })
                                        )
                                    )
                                )
                            )
                        ),
                        m('.col-12', m(paginationComponent,
                            {
                                currentPage: pagination.pageIndex,
                                pageSize: pagination.pageSize,
                                totalItems: pagination.totalItems,
                                onPageChanged: (pageIndex) => {
                                    pagination.pageIndex = pageIndex;
                                    getModelList();
                                },
                                onPageSizeChanged: (_, pageSize) => {
                                    pagination.pageIndex = DEFAULT_PAGINATION.pageIndex
                                    pagination.pageSize = pageSize;
                                    getModelList();
                                }
                            }
                        ))
                    )
                ),
                isModelInformationOpen && m(ModelInformationDialog, { modelCode: selectedModelCode, onClose: closeModelInformation }),
                this.getBaseViews()
            ];
        }
    };
}
